import { CSSProperties, useCallback, useEffect } from 'react';
import {
  commonStyleHandlers,
  ContainerResponsiveProp,
  Responsive,
  SapphireCommonStyleProps,
  StyleHandler,
  StyleHandlers,
} from './SapphireStyleProps';
import { type BreakpointKey } from './useBreakpointTokens';
import { useBreakpointContext } from '../BreakpointProvider';
import { useId } from '@react-aria/utils';
import clsx from 'clsx';

const isSapphireToken = (obj: Record<string, unknown>): obj is SapphireToken =>
  'value' in obj;

export const resolveResponsiveValue = <
  T extends string | number | undefined | SapphireToken
>(
  matchedBreakpoints: BreakpointKey[],
  value?: Responsive<T | SapphireToken>
): T | string => {
  if (value && typeof value === 'object') {
    // Handle SapphireToken case like `{ value: 'var(--sapphire-semantic-size-width-panel-sm)' }`
    if (isSapphireToken(value)) {
      return (value as SapphireToken).value;
    }
    for (let i = 0; i < matchedBreakpoints.length; i++) {
      const breakpoint = matchedBreakpoints[i];
      const breakpointValue = value[breakpoint];
      if (breakpointValue != null) {
        // Handle case where a breakpoint has a SapphireToken like `{ sm: { value: 'var(--sapphire-semantic-size-width-panel-sm)' } }`
        if (
          typeof breakpointValue === 'object' &&
          isSapphireToken(breakpointValue)
        ) {
          return breakpointValue.value;
        } else {
          // Handle case where a breakpoint has a 'string' | 'number' | 'undefined' value like `{ sm: '11px' }`
          return breakpointValue as T;
        }
      }
    }
  }
  // Handle 'string' | 'number' | 'undefined' case
  return value as T;
};

// Unsure why an interface makes the typeguard fail - perhaps something that is fixed in later TS versions than 4.9?
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SapphireToken = {
  value: string;
};

export const useResolveResponsiveValue = (): (<
  T extends string | number | undefined
>(
  value?: Responsive<T | SapphireToken>
) => T | string) => {
  const breakpointContext = useBreakpointContext();
  const matchedBreakpoints =
    (breakpointContext?.matchedBreakpoints as BreakpointKey[]) || ['base'];
  return (value) => resolveResponsiveValue(matchedBreakpoints, value);
};

const generateRulePart = <T extends string | number | undefined>(
  containerBreakpointValue: number,
  className: string,
  key: string,
  breakpointResponsiveValue: T | SapphireToken,
  container: true | string
) => {
  const kebabCaseKey = key.replace(
    /[A-Z]/g,
    (match) => `-${match.toLowerCase()}`
  );

  const value =
    breakpointResponsiveValue &&
    typeof breakpointResponsiveValue === 'object' &&
    isSapphireToken(breakpointResponsiveValue)
      ? breakpointResponsiveValue.value
      : breakpointResponsiveValue;

  return `
    @container ${
      typeof container === 'string' ? container : ''
    } (min-width: ${containerBreakpointValue}px) {
      .${className} {
        ${kebabCaseKey}: ${value};
      }
    }`;
};

/**
 * The `useResolveContainerQuery` hook leverages the power of CSS container queries to dynamically
 * generate and apply styles based on the provided responsive values in relation to the specified
 * container.
 *
 * This hook allows collecting the key-value pairs necessary for generating a new stylesheet that
 * will provide the necessary container queries based on the passed values and appends it to the
 * document's head.
 *
 * By using CSS container queries, we avoid emulating the behavior in JavaScript.
 */
export const useContainerStyleProps = <T extends SapphireCommonStyleProps>(
  props: Omit<T & SapphireStyleProps, 'UNSAFE_style' | 'UNSAFE_className'>,
  handlers: StyleHandlers<T>
): {
  containerStylesClassName: string;
} => {
  const uniqueId = useId().replace(/:/g, '_');
  // There should only be one classname per element
  const elementClassName = `container-query-${uniqueId}`;
  const breakpointContext = useBreakpointContext();

  let stylesString = '';
  Object.keys(props).forEach((_key) => {
    const key = _key as Exclude<keyof T, 'UNSAFE_style' | 'UNSAFE_className'>;
    const responsiveValue = props[key];
    if (!responsiveValue || !isContainerResponsiveProp(responsiveValue)) {
      return;
    }

    const breakpoints = [
      0, // `value.base` should be mapped to min-width: 0px and should always be first in the stylesheet.
      ...Object.keys(responsiveValue).filter(
        (breakpointKey) => !['base', 'container'].includes(breakpointKey)
      ),
    ];

    breakpoints.forEach((breakpointKey) => {
      // Try resolving the current breakpoint as a number. If it doesn't work, it's for sure a design-token breakpoint.
      const isDesignTokenBreakpoint = isNaN(Number(breakpointKey));
      const containerBreakpointValue = isDesignTokenBreakpoint
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- we are confident it's a BreakpointKey
          breakpointContext?.breakpoints?.[breakpointKey as BreakpointKey]!
        : Number(breakpointKey);

      const value = (
        isDesignTokenBreakpoint
          ? responsiveValue[breakpointKey as BreakpointKey]
          : breakpointKey === 0
          ? responsiveValue.base
          : responsiveValue[containerBreakpointValue]
      ) as string | number | undefined | null;
      Object.entries(normalizeHandler(handlers[key], value)).forEach(
        ([styleProp, styleValue]) => {
          stylesString += generateRulePart(
            containerBreakpointValue,
            elementClassName,
            styleProp,
            styleValue,
            responsiveValue.container
          );
        }
      );
    });
  });

  const getOrCreateStylesheet = useCallback(() => {
    const stylesheetElementId = 'container-query-stylesheet-' + uniqueId;
    let containerQueryStylesheetElement =
      document.getElementById(stylesheetElementId);
    if (!containerQueryStylesheetElement) {
      containerQueryStylesheetElement = document.createElement('style');
      containerQueryStylesheetElement.id = stylesheetElementId;
      document.head.appendChild(containerQueryStylesheetElement);
    }
    return containerQueryStylesheetElement as HTMLStyleElement;
  }, [uniqueId]);

  /**
   * Create or update the stylesheet with the generated container query styles.
   *
   * The stylesheet is only created if styles are provided.
   * The stylesheet is removed when the component using the hook is unmounted.
   */
  useEffect(() => {
    if (!stylesString) {
      return;
    }

    const styleSheet = getOrCreateStylesheet();
    const cssRuleNode = document.createTextNode(stylesString);
    styleSheet.appendChild(cssRuleNode);
    return () => {
      styleSheet.remove();
    };
  }, [stylesString, getOrCreateStylesheet]);

  return {
    containerStylesClassName: stylesString ? elementClassName : '',
  };
};

interface RegularStyleProps {
  style?: CSSProperties;
  className?: string;
}

interface UnsafeStyleProps {
  UNSAFE_className?: string;
  UNSAFE_style?: CSSProperties;
}

export interface SapphireStyleProps
  extends SapphireCommonStyleProps,
    UnsafeStyleProps {}

export type FilteredProps<P, T> = Omit<
  P,
  keyof (T & RegularStyleProps & UnsafeStyleProps)
>;

function isContainerResponsiveProp<T>(
  prop: any
): prop is ContainerResponsiveProp<T> {
  return prop && typeof prop === 'object' && 'container' in prop;
}

export const useSapphireStyleProps = <
  P extends T,
  T extends SapphireCommonStyleProps = SapphireCommonStyleProps
>(
  { UNSAFE_className, UNSAFE_style, ...props }: P & SapphireStyleProps,
  handlers: StyleHandlers<T> = commonStyleHandlers as StyleHandlers<T>
): {
  styleProps: { style: CSSProperties; className?: string };
  filteredProps: FilteredProps<P, T>;
} => {
  const resolveResponsiveValue = useResolveResponsiveValue();
  const { containerStylesClassName } = useContainerStyleProps<T>(
    props,
    handlers
  );
  const derivedStyle: CSSProperties = {};

  const filteredProps = {} as FilteredProps<P, T>;

  (Object.keys(props) as Array<keyof typeof props>).forEach((key) => {
    if (
      ['style', 'className'].includes(key as string) &&
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV === 'development'
    ) {
      // eslint-disable-next-line no-console
      return console.warn(
        "Passing 'style' or 'className' props will have no effect. Please use supported style props directly or use UNSAFE_style or UNSAFE_className if you have no other options than applying style directly to the component."
      );
    }

    const currentProp = props[key];
    const handler = handlers[key as keyof T];
    if (handler !== undefined && currentProp !== null) {
      if (!isContainerResponsiveProp<T>(currentProp)) {
        /**
         * If no context is provided, we treat it as a viewport query.
         */
        const resolvedResponsiveValue = resolveResponsiveValue(
          // @ts-expect-error: TS2590: Expression produces a union type that is too complex to represent.
          currentProp as Responsive<SapphireToken | string | number | undefined>
        );
        Object.assign(
          derivedStyle,
          normalizeHandler(handler, resolvedResponsiveValue)
        );
      }
    } else {
      // @ts-expect-error: TS2536: Cannot be used to index type 'Omit<P, keyof T>'.
      // TS is not convinced that we're not working with T at this point. We are.
      filteredProps[key] = currentProp;
    }
  });

  return {
    styleProps: {
      style: { ...UNSAFE_style, ...derivedStyle },
      className: clsx(UNSAFE_className, containerStylesClassName),
    },
    filteredProps,
  };
};

function normalizeHandler<T extends keyof CSSProperties>(
  handler: StyleHandler<T>,
  value: string | number | undefined | null
): CSSProperties {
  const result: CSSProperties = {};
  if (value == undefined) {
    return result;
  }
  if (typeof handler === 'object') {
    if ('convertValue' in handler) {
      value = handler.convertValue(value);
    }
    const styleProps =
      'styleProps' in handler ? handler.styleProps : [handler.styleProp];
    styleProps.forEach((styleProp) => {
      result[styleProp as keyof CSSProperties] = value;
    });
  } else {
    // @ts-expect-error: TS2590: Expression produces a union type that is too complex to represent.
    result[handler as keyof CSSProperties] = value;
  }

  return result;
}
